// API contract types. Mirrors backend serializers.

/** Standard DRF page envelope — `{count, next, previous, results}`.
 *  Returned by every paginated list endpoint (apps/pagination.py).
 *  `next` / `previous` are absolute URLs or null. */
export interface Paginated<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

/** Per-attempt status for a CampaignMembership. Legacy alias kept as
 *  `LeadStatus` so existing SPA code that imports it keeps compiling. */
export type LeadStatus =
  | "new"
  | "contacted"
  /** Every channel failed — nobody reached them. Our problem to retry,
   *  not the beneficiary's answer. */
  | "contact_failed"
  | "scheduled"
  | "confirmed"
  | "session_done"
  | "no_show"
  | "declined";

/** Question — Phase B of CAMPAIGN_LEADS_QUESTIONS.md.
 *  Ten field kinds (see §3.5 of the doc). `options` only populated for
 *  dropdown/radio/checkboxes. Numeric constraints only for `number`. */
export type QuestionKind =
  | "text"
  | "textarea"
  | "number"
  | "dropdown"
  | "radio"
  | "checkboxes"
  | "boolean"
  /** Backlog 2026-09-18 — same Yes/No, plus an optional comment box
   *  shown per `comment_trigger`: "always" (default) or gated on one
   *  answer. Blank is treated as "always". */
  | "boolean_comment"
  /** Requested 2026-10-01 — Yes / No / NSP ("ne sait pas", I don't
   *  know). A blank answer and an NSP are different facts: blank means
   *  nobody got there, NSP means somebody did and could not tell. */
  | "tristate"
  | "tristate_comment"
  | "date"
  | "rating";

/** The three values a tri-state question accepts on the wire. */
export const TRISTATE_VALUES = ["yes", "no", "nsp"] as const;
export type TristateValue = (typeof TRISTATE_VALUES)[number];

/** Kinds that render a Yes/No-style choice. */
export const BOOLEANISH_KINDS: readonly QuestionKind[] = [
  "boolean",
  "boolean_comment",
  "tristate",
  "tristate_comment",
];

/** Kinds that carry an operator comment box. */
export const COMMENT_KINDS: readonly QuestionKind[] = [
  "boolean_comment",
  "tristate_comment",
];

/** Kinds that can be flagged as a conformity check. An NSP is never the
 *  conforming answer, so it makes the session non-conform — that falls
 *  out of the value simply not matching `conformity_value`. */
export const CONFORMITY_KINDS: readonly QuestionKind[] = BOOLEANISH_KINDS;

export type QuestionOption = { value: string; label: string };

export type Question = {
  id: string;
  campaign: string;
  order: number;
  label: string;
  help_text: string;
  kind: QuestionKind;
  required: boolean;
  options: QuestionOption[];
  min_value: string | null;
  max_value: string | null;
  step: string | null;
  placeholder: string;
  /** The "+ comment" kinds only — which answer reveals the comment box.
   *  "always" (the default) shows it unconditionally. "nsp" is valid
   *  only on tristate_comment; "" for every kind without a box. */
  comment_trigger: "always" | "yes" | "no" | "nsp" | "";
  /** Backlog 2026-09-21 — the Yes/No kinds only (tri-state included).
   *  Flags this
   *  question as one of the campaign's compliance checks; the campaign
   *  report tallies how many sessions come out conform/non-conform
   *  against it. `false` for every other kind. */
  is_conformity: boolean;
  /** Which answer counts as "conform" — "yes" or "no". "" when
   *  is_conformity is false. Defaults to "yes" when first enabled. */
  conformity_value: "yes" | "no" | "";
  archived_at: string | null;
  created_at: string;
  updated_at: string;
};

export type InspectionAnswer = {
  id: string;
  session: string;
  question: string | null;
  /** Backlog 2026-09-18 — null for the ordinary single-site call; an
   *  Address id when this answer belongs to one of the beneficiary's
   *  several project sites (see the session-qa `sites` list). */
  site: string | null;
  question_label_snapshot: string;
  question_kind_snapshot: QuestionKind;
  /** Populated for text/textarea/dropdown/radio/date and every Yes/No
   *  kind — "yes", "no", or "nsp" for the tri-states. */
  value_text: string;
  /** Populated for number/rating. Decimal string from the wire. */
  value_number: string | null;
  /** Populated for checkboxes (array of selected option values). */
  value_json: string[];
  /** The "+ comment" kinds only. "" for every other kind. */
  comment: string;
  answered_by: number | null;
  answered_at: string;
};

/** R1+R2 of CONTACTS_REFACTOR — a Contact is the long-lived person
 *  record. Same person can be enrolled in multiple campaigns via
 *  CampaignMembership; the Membership carries per-attempt status. */
/** P2 of ADDRESS_AND_CUSTOM_FIELDS — legal entity flag + curated
 *  business-type enum on Contact. */
export type BusinessType =
  | ""
  | "hotel"
  | "restaurant"
  | "office"
  | "retail"
  | "warehouse"
  | "factory"
  | "hospital"
  | "school"
  | "hoa"
  | "syndic_co"
  | "ngo"
  | "public"
  | "other";

export type Contact = {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  address_line: string;
  city: string;
  postal_code: string;
  country: string;
  external_ref: string;
  language: string;
  is_business: boolean;
  business_type: BusinessType;
  /** Company identity (legal entity only). */
  legal_name: string;
  registration_number: string;
  vat_id: string;
  /** Company point-of-contact person (legal entity only) — distinct from
   *  the beneficiary's own first/last/email/phone above. */
  company_contact_name: string;
  company_contact_email: string;
  company_contact_phone: string;
  is_invalid: boolean;
  do_not_call: boolean;
  do_not_email: boolean;
  gdpr_erasure_requested_at: string | null;
  /** P4 — per-org custom fields (DNI, CIF, SIRET, etc.). Shape
   *  governed by FieldDefinition rows; raw write writes the dict and
   *  the backend validates it. */
  custom_fields_jsonb: Record<string, unknown>;
  membership_count: number;
  session_count: number;
  last_membership_status: string;
  /** P1 — multi-address per contact. The serializer inlines the
   *  rows so the SPA doesn't need a second round-trip. */
  addresses: Address[];
  primary_address: Address | null;
  created_at: string;
  updated_at: string;
};

/** P5 — payload of /v1/admin/address-schemas/. Each country's schema
 *  declares required + optional flat fields, the country-specific
 *  JSONB sub-fields, an optional postal_code regex, and a render
 *  template. Keyed by ISO-3166 alpha-2 plus a "_generic" fallback. */
export type CountrySpecificField = {
  label_en?: string;
  /** Country-native label (label_es / label_fr / label_de / label_it). */
  [otherLabelKey: string]: string | boolean | string[] | undefined;
  kind: "select" | "text";
  choices?: string[];
  required?: boolean;
  auto_from_postal?: boolean;
  auto_from?: string;
};

export type AddressCountrySchema = {
  required: string[];
  optional: string[];
  country_specific: Record<string, CountrySpecificField>;
  postal_regex?: string;
  format?: string;
};

export type AddressSchemaCatalog = Record<string, AddressCountrySchema>;

/** P1 of ADDRESS_AND_CUSTOM_FIELDS — country-aware structured
 *  address. The `formatted` field is the country-specific render the
 *  SPA + PDF display directly. */
export type AddressPurpose =
  | "beneficiary"
  | "worksite"
  | "billing"
  | "other";

export type Address = {
  id: string;
  contact: string;
  purpose: AddressPurpose;
  is_primary: boolean;
  label: string;
  street_type: string;
  street_name: string;
  street_number: string;
  sub_unit: string;
  locality: string;
  region: string;
  postal_code: string;
  country: string;
  country_specific_jsonb: Record<string, unknown>;
  notes: string;
  building_name: string;
  latitude: string | null;
  longitude: string | null;
  geocoded_at: string | null;
  legacy_unparsed: boolean;
  custom_fields_jsonb: Record<string, unknown>;
  formatted: string;
  created_at: string;
  updated_at: string;
};

/** P3 — curated CEE/CAE scheme category. Drives the bundle
 *  validator (required roles + fields). */
export type ProjectCategory =
  | "es_res20"
  | "es_res60"
  | "es_ter20"
  | "es_ind"
  | "fr_bar"
  | "fr_bat"
  | "fr_bar_tertiaire"
  | "fr_ind"
  | "generic"
  | "other";

/** P2 — the role a Contact plays on a specific Membership. */
export type MembershipPartyRole =
  | "beneficiary"
  | "ceo"
  | "legal_rep"
  | "syndic"
  | "owner"
  | "project_manager"
  | "architect"
  | "other";

export type MembershipParty = {
  id: string;
  membership: string;
  contact: string;
  contact_first_name: string;
  contact_last_name: string;
  contact_email: string;
  contact_is_business: boolean;
  contact_business_type: BusinessType;
  role: MembershipPartyRole;
  notes: string;
  custom_fields_jsonb: Record<string, unknown>;
  created_at: string;
  updated_at: string;
};

/** P4 — per-org custom field definition. */
export type FieldKind =
  | "text"
  | "textarea"
  | "number"
  | "boolean"
  | "date"
  | "dropdown"
  | "checkboxes"
  | "email"
  | "regex";

export type FieldAppliesTo =
  | "contact"
  | "address"
  | "membership"
  | "party";

/** P7 — async ImportJob shape returned by /v1/campaigns/<id>/memberships/
 *  imports/<job_id>. The SPA polls this until `is_terminal` (status
 *  in the four terminal values) then renders the result. */
export type ImportJobStatus =
  | "awaiting_mapping"
  | "awaiting_review"
  | "queued"
  | "processing"
  | "succeeded"
  | "failed_validation"
  | "failed_fatal"
  | "error";

/** Conflict review — a multi-site workbook has one row per SITE, so the
 *  rows that land on one beneficiary can disagree. Nothing is written
 *  until these are decided. See docs/IMPORT_CONFLICT_RESOLVER.md. */
export type ConflictKind = "identity" | "value";
export type ConflictScope = "column" | "beneficiary";
export type ResolutionAction =
  | "merge"
  | "split"
  | "skip"
  | "keep_first"
  | "keep_last"
  | "remap";

export type ConflictVariant = {
  value: string;
  rows: number[];
  addresses: string[];
};

export type ImportConflict = {
  id: string;
  kind: ConflictKind;
  scope: ConflictScope;
  field: string;
  column_label: string;
  default_action: ResolutionAction;
  /** Pre-decided by the matching rules. NOT hidden — it arrives
   *  answered and one click from being overridden. */
  auto: boolean;
  channel: string;
  variants: ConflictVariant[];
  beneficiaries_affected: number;
  examples: { channel: string; values: string[] }[];
  verdict: string;
  fingerprint: string;
  split_blocked_reason: string;
  /** `uniq_contact_org_email` makes a populated email unique per org,
   *  so only one of the split beneficiaries can keep the shared one. */
  split_needs_new_email: boolean;
  allowed_actions: ResolutionAction[];
  resolution: { action: ResolutionAction } | null;
};

export type ImportConflictsResponse = {
  status: ImportJobStatus;
  summary: {
    total: number;
    columns: number;
    beneficiaries: number;
    auto: number;
    needs_decision: number;
  };
  conflicts: ImportConflict[];
};

export type ImportJobRowError = {
  excel_row: number;
  field: string;
  message: string;
};

export type ImportJobSkipped = {
  first_name: string;
  last_name: string;
  reason: string;
};

export type ImportJob = {
  id: string;
  campaign: string;
  uploaded_by: number | null;
  uploaded_by_email: string | null;
  source_filename: string;
  source_sha256: string;
  source_size_bytes: number;
  status: ImportJobStatus;
  fatal_error: string;
  row_count: number;
  error_count: number;
  contacts_created: number;
  contacts_updated: number;
  memberships_created: number;
  prefilled_answer_count: number;
  /** Worksites this beneficiary has. A multi-site import makes ONE
   *  contact with N sites, and the row gave no hint of that. */
  contact_site_count?: number;
  errors_jsonb: ImportJobRowError[];
  skipped_jsonb: ImportJobSkipped[];
  warnings_jsonb: string[];
  has_error_file: boolean;
  queued_at: string;
  started_at: string | null;
  finished_at: string | null;
  updated_at: string;
};

/** Campaign report — counts + per-question aggregates. */
export type CampaignReportTotals = {
  memberships: number;
  memberships_by_status: Record<string, number>;
  sessions_total: number;
  sessions_done: number;
  sessions_open: number;
  /** Backlog 2026-09-21 — how many sessions matched every configured
   *  is_conformity question's conformity_value / didn't / haven't
   *  answered any conformity question yet. All zero when the campaign
   *  has no conformity questions configured. */
  sessions_conform: number;
  sessions_non_conform: number;
  sessions_conformity_pending: number;
};

export type CampaignReportQuestion = {
  question_id: string;
  label: string;
  kind: string;
  order: number;
  required: boolean;
  options: Array<{ value: string; label: string }>;
  answered_count: number;
  /** Categorical kinds (boolean / dropdown / radio / checkboxes / rating). */
  breakdown?: Record<string, number> | null;
  /** Numeric kind. */
  numeric?: {
    min: number | null;
    max: number | null;
    avg: number | null;
    count: number;
  };
};

export type CampaignReport = {
  campaign_id: string;
  campaign_name: string;
  totals: CampaignReportTotals;
  questions: CampaignReportQuestion[];
};


/** DataSure SecuSign per-document signature flow. */
export type SignatureTransactionStatus =
  | "queued"
  | "created"
  | "sent"
  | "completed"
  | "cancelled"
  | "refused"
  | "failed";

export type SignatoryStatus =
  | "pending"
  | "created"
  | "signed"
  | "refused"
  | "failed";

export type Signatory = {
  id: string;
  order: number;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  language: string;
  authentication_type: "sms" | "email";
  automation: boolean;
  status: SignatoryStatus;
  datasure_uid: string | null;
  signatory_url: string;
  sent_at: string | null;
  signed_at: string | null;
  refused_at: string | null;
  invitation_sent_at: string | null;
};

export type SignatureTransaction = {
  id: string;
  session: string | null;
  source_evidence_id: string;
  signed_pdf_evidence_id: string | null;
  created_by: number | null;
  created_by_email: string | null;
  language: string;
  status: SignatureTransactionStatus;
  is_terminal: boolean;
  datasure_uid: string | null;
  fatal_error: string;
  ds_created_at: string | null;
  sent_at: string | null;
  completed_at: string | null;
  cancelled_at: string | null;
  signatories: Signatory[];
  created_at: string;
  updated_at: string;
};

export type FieldDefinition = {
  id: string;
  org: number | null;
  key: string;
  label: string;
  help_text: string;
  kind: FieldKind;
  applies_to: FieldAppliesTo;
  country_scope: string;
  project_category_scope: ProjectCategory | "";
  role_scope: MembershipPartyRole | "";
  is_required: boolean;
  is_system: boolean;
  is_org_override: boolean;
  is_disabled: boolean;
  is_pii: boolean;
  options_jsonb: Array<{ value: string; label: string }>;
  regex_pattern: string;
  min_value: number | null;
  max_value: number | null;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
};

export type MembershipStatus =
  | "new"
  | "contacted"
  | "scheduled"
  | "confirmed"
  | "session_done"
  | "no_show"
  | "declined";

export type CampaignMembership = {
  id: string;
  campaign: string;
  campaign_name: string;
  contact: string;
  contact_first_name: string;
  contact_last_name: string;
  contact_email: string;
  contact_phone: string;
  contact_city: string;
  contact_country: string;
  contact_is_invalid: boolean;
  contact_do_not_call: boolean;
  contact_is_business: boolean;
  contact_business_type: BusinessType;
  status: MembershipStatus;
  outcome_reason: string;
  /** Per-campaign (membership-scoped) custom-field values. */
  custom_fields_jsonb: Record<string, unknown>;
  assigned_to: number | null;
  assigned_to_email: string | null;
  notes_intake: string;
  prefilled_answer_count: number;
  /** Worksites this beneficiary has. A multi-site import makes ONE
   *  contact with N sites, and the row gave no hint of that. */
  contact_site_count?: number;
  first_contacted_at?: string | null;
  last_contacted_at?: string | null;
  /** Precisely what reached them last: "wave" | "manual_wave" |
   *  "operator" | "ai". Blank for anybody reached before this was
   *  recorded — which is not the same as never reached. */
  last_contacted_source?: string;
  /** The two-bucket answer the list shows: "system" | "operator", or ""
   *  when the source is unknown. Keyed on who DECIDED, so an operator
   *  pressing "Send wave now" reads as "operator". */
  contacted_by?: "system" | "operator" | "";
  worksite_address: string | null;
  worksite_address_detail: Address | null;
  parties: MembershipParty[];
  project_category: ProjectCategory;
  project_code: string;
  // Phase 2D-sched — open session metadata exposed by the
  // CampaignMembershipSerializer so the leads table can render a
  // "Scheduled at" column + a Reschedule button without a per-row
  // session lookup.
  next_scheduled_for: string | null;
  next_session_id: string | null;
  next_session_notes: string | null;
  // Phase 2E-camp-mr — soft-archive metadata + disabled-state signal.
  is_archived: boolean;
  archived_at: string | null;
  removed_by_email: string | null;
  removed_reason: string;
  has_active_session: boolean;
  created_at: string;
  updated_at: string;
};

/** Lead — alias for the per-row shape returned by /campaigns/{id}/memberships/.
 *  After the Contacts refactor the row's identity fields are joined from
 *  the linked Contact (contact_first_name, etc.). The non-prefixed fields
 *  are kept as derived getters so existing SPA components keep working. */
export type Lead = CampaignMembership & {
  /** Derived from contact_first_name for legacy callers — populated by
   *  the API serializer. */
  readonly first_name?: string;
  readonly last_name?: string;
  readonly email?: string;
  readonly phone?: string;
  readonly city?: string;
  readonly country?: string;
};

export type Role = {
  id: number;
  slug: string;
  name: string;
  description: string;
  is_system: boolean;
  permissions: string[];
  created_at: string;
  updated_at: string;
};

export type Organization = {
  id: number;
  name: string;
  country: string;
  /** IANA time zone the org operates in (e.g. "Europe/Paris"). The platform
   *  stores timestamps in UTC; beneficiary messages render times in this tz
   *  (a campaign can override it). Set via PATCH /v1/orgs/me/timezone. */
  timezone?: string;
  /** Campaigns/Sessions v2 — org logo for the switcher dropdown. */
  logo_url?: string;
  settings_jsonb: Record<string, unknown>;
  /** GDPR Art. 5(1)(e) storage-limitation. `null` = no auto-deletion;
   *  any positive int triggers the nightly retention sweep that purges
   *  Sessions older than this many days (skipping legal_hold rows).
   *  Set via PATCH /v1/orgs/me/retention. */
  retention_days: number | null;
  /** Phase 2D-sched-d — opt-in flag for SMS invite/reminder delivery.
   *  Default false because SMS is metered (Twilio bills per send).
   *  Set via PATCH /v1/orgs/me/sms. */
  sms_enabled: boolean;
  /** Default true. When true, emailed/SMSed invites get a 6-digit OTP
   *  the field user must enter on /join. When false, the signed
   *  invite URL alone authorises the join (low-friction b2c flows).
   *  Set via PATCH /v1/orgs/me/otp. */
  require_otp_for_invites: boolean;
  /** Default true. When true, the field user MUST share a working GPS
   *  fix before they can join an inspection (the /join consent gate
   *  blocks + guides them to enable location). False relaxes it to
   *  best-effort. Set via PATCH /v1/orgs/me/geo-required. */
  require_field_geo: boolean;
  /** Operator call banner — how long BEFORE a scheduled inspection the
   *  warning appears, and how long it survives AFTER the slot passes.
   *  Grace may legitimately be 0 ("drop it at the slot time"). */
  call_alert_lead_minutes: number;
  call_alert_grace_minutes: number;
  created_at: string;
};

export type Membership = {
  id: number;
  org: Organization;
  roles: string[];
  is_active: boolean;
  last_active_at: string | null;
  created_at: string;
};

export type User = {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  mfa_enabled: boolean;
  is_active: boolean;
  last_login: string | null;
};

// Phase 3-platform — super-admin console.
export type PlatformMe = {
  is_platform_staff: boolean;
  mfa_enabled: boolean;
  email?: string;
};

export type PlatformKpis = {
  total_orgs: number;
  verified_orgs: number;
  flagged_orgs: number;
  open_sessions: number;
};

export type PlatformOrgRow = {
  id: number;
  name: string;
  legal_name: string;
  country: string;
  kyc_status: string;
  sanctions_match_found: boolean;
  sanctions_screened_at: string | null;
  created_at: string;
};

export type PlatformOrgListResponse = {
  results: PlatformOrgRow[];
  has_more: boolean;
  next_cursor: string;
};

export type PlatformOrgDetail = {
  id: string;
  name: string;
  legal_name: string;
  country: string;
  created_at: string | null;
  kyc_status: string;
  kyc_attested_at: string | null;
  vat_id: string;
  vat_validated_name: string;
  registration_jurisdiction: string;
  registration_number: string;
  registry_validated_name: string;
  registry_is_active: boolean;
  sanctions_match_found: boolean;
  sanctions_screened_at: string | null;
  sanctions_force_clean_until: string | null;
  sanctions_matches: Array<Record<string, unknown>>;
  blockchain_immediate: boolean;
  eidas_scope: "final_only" | "everything";
  member_count: number;
  session_count: number;
  open_session_count: number;
  subscription: {
    plan_slug: string;
    status: string;
    seat_count: number;
    current_period_end: string | null;
  } | null;
  open_usage_minutes: number;
};

export type PlatformAuditEventRow = {
  id: string;
  occurred_at: string;
  kind: string;
  actor_user: number | null;
  actor_user_email: string | null;
  target_org: number | null;
  target_org_name: string | null;
  payload: Record<string, unknown>;
};

export type MePayload = {
  user: User;
  active_org: Organization | null;
  active_org_id: number | null;
  memberships: Membership[];
  permissions: string[];
};

export type TokenPair = {
  access: string;
  refresh: string;
  org_id: number | null;
};

// ---------- Reseller/commercial partner console ----------
export type PartnerProfile = {
  kind: "reseller" | "commercial";
  id: string;
  name: string;
  referral_code: string;
  reseller_id: string | null;
  /** Reseller-only: prefix on their client-invoice numbers (empty = derived
   * from slug). */
  invoice_prefix?: string;
  terms: {
    rate_base_pct: string;
    rate_session_pct: string;
    rate_signature_pct: string;
    commission_duration: string;
  };
  /** Reseller-only: "commission" | "wholesale" ("" for commercials). */
  billing_mode?: string;
  /** Reseller-only wholesale € rates ({} for commercials / commission). */
  wholesale?: {
    session_eur: string | null;
    signature_l1_eur: string | null;
    signature_l2_eur: string | null;
    signature_l3_eur: string | null;
    base_monthly_eur: string | null;
  };
  organization_count: number;
  payouts: {
    is_payee: boolean;
    has_account: boolean;
    payouts_enabled: boolean;
  };
};

/** A wholesale reseller's P&L (Phase 3-invoicing P4.5). Empty object for
 *  commission resellers. */
export type ResellerEconomics = {
  revenue_eur?: string;
  owed_nexbasira_eur?: string;
  owed_nexbasira_pending_eur?: string;
  owed_commercials_eur?: string;
  margin_eur?: string;
  commercials?: { name: string; owed_eur: string }[];
  client_invoices?: import("./types").BillingInvoice[];
};


/** An org's invoice (Phase 3-invoicing). */
export type BillingInvoice = {
  id: string;
  /** Sequential invoice number (empty for legacy invoices). */
  number?: string;
  period_start: string;
  period_end: string;
  currency: string;
  subtotal_eur: string;
  tax_eur: string;
  total_eur: string;
  status: "draft" | "open" | "paid" | "void" | "uncollectible";
  hosted_url: string;
  pdf_url: string;
  issued_at: string | null;
  due_at: string | null;
  paid_at: string | null;
  lines: {
    kind: string;
    description: string;
    quantity: string;
    unit_price_eur: string;
    amount_eur: string;
  }[];
};


/** One buyable pre-paid signature pack from the platform catalog. */
export type SignaturePackOption = {
  key: string;
  level: "l1" | "l2" | "l3";
  label: string;
  quantity: number;
  setups_included: number;
  price_eur: string;
  /** False when this deployment hasn't configured the pack's Stripe price. */
  purchasable: boolean;
};

/** A pack the reseller can grant (partner console) — wholesale cost view. */
export type PartnerPackOption = {
  key: string;
  level: "l1" | "l2" | "l3";
  label: string;
  quantity: number;
  /** Wholesale cost to the reseller; null when it passes through at the
   * org's plan price (shown as the exact charge at grant time). */
  wholesale_price_eur: string | null;
};

/** A managed org's signature-pack request (org sees status; reseller sees inbox). */
export type SignaturePackRequest = {
  id: string;
  org_id: number;
  org_name: string;
  pack_key: string;
  level: string;
  quantity: number;
  setups: number;
  status: "pending" | "granted" | "declined";
  note: string;
  requested_by_email: string;
  created_at: string;
  resolved_at: string | null;
};


export type OrgAddress = {
  line1?: string;
  line2?: string;
  city?: string;
  postal_code?: string;
  state?: string;
  country?: string;
};

export type OrgContact = {
  id?: number;
  name: string;
  email?: string;
  phone?: string;
  designation?: string;
  is_default?: boolean;
};

export type PartnerOrg = {
  id: string;
  name: string;
  country: string;
  phone?: string;
  address?: OrgAddress;
  contacts?: OrgContact[];
  custom_price_per_session_eur: string | null;
  included_signatures_per_session: number;
  included_signature_level: string;
  price_per_signature_overflow_eur?: string | null;
  commissionable_price_per_session_eur?: string | null;
  legal_name?: string;
  registration_number?: string;
  naf_code?: string;
  vat_id?: string;
  reseller_id: string | null;
  commercial_id: string | null;
  created_at: string;
  campaigns_count: number;
  sessions_count: number;
  direct_sessions_count: number;
  /** Signature-pack credits granted to this org: used / total. */
  signature_credits_total?: number;
  signature_credits_used?: number;
  /** Current billing plan (managed orgs are provisioned by the reseller,
   *  not via self-serve Checkout). */
  current_plan_slug?: string;
  current_plan_name?: string;
  /** Message of the customer's open plan-change request, if any (null when
   *  none outstanding). The reseller fulfils it by setting a plan. */
  pending_plan_request?: string | null;
  /** True only while this org's own admin has an unactivated account (invited
   *  but never logged in). Drives the "resend invite" button — hidden once the
   *  admin has activated, or when the org has no own admin. */
  admin_invite_pending?: boolean;
};

export type CommissionEntryRow = {
  id: string;
  org_id: number;
  period_start: string;
  period_end: string;
  base_revenue_eur: string;
  session_revenue_eur: string;
  signature_revenue_eur: string;
  commission_eur: string;
  status: string;
  created_at: string;
};

export type PartnerCommissions = {
  entries: CommissionEntryRow[];
  unpaid_total_eur: string;
};

// ---------- Super-admin partner management ----------
export type PlatformPartnerCommercial = {
  id: string;
  name: string;
  email: string;
  status: string;
  reseller_id: string | null;
  referral_code: string;
  commission_model: "percentage" | "fixed";
  rate_base_pct: number;
  rate_session_pct: number;
  rate_signature_pct: number;
  fixed_base_eur: number;
  fixed_session_eur: number;
  fixed_signature_eur: number;
  commission_duration: string;
  org_count: number;
  payouts_enabled: boolean;
  commission_total_eur: number;
  commission_unpaid_eur: number;
};

export type PlatformReseller = {
  id: string;
  name: string;
  slug: string;
  status: string;
  referral_code: string;
  commission_model: "percentage" | "fixed";
  rate_base_pct: number;
  rate_session_pct: number;
  rate_signature_pct: number;
  fixed_base_eur: number;
  fixed_session_eur: number;
  fixed_signature_eur: number;
  commission_duration: string;
  org_count: number;
  payouts_enabled: boolean;
  commission_total_eur: number;
  commission_unpaid_eur: number;
  commercials: PlatformPartnerCommercial[];
};

export type PlatformPartnersResponse = {
  resellers: PlatformReseller[];
  direct_commercials: PlatformPartnerCommercial[];
};

export type CommercialBreakdownRow = {
  id: string;
  name: string;
  email: string;
  phone?: string;
  is_company?: boolean;
  legal_name?: string;
  registration_number?: string;
  vat_id?: string;
  address_line?: string;
  city?: string;
  postal_code?: string;
  address_country?: string;
  referral_code: string;
  status: string;
  /** Whether the commercial has an activated login account. */
  has_login?: boolean;
  /** Whether an account-opening invite is outstanding (sent, not accepted). */
  invite_pending?: boolean;
  commission_model: "percentage" | "fixed";
  rate_base_pct: string;
  rate_session_pct: string;
  rate_signature_pct: string;
  fixed_base_eur: string;
  fixed_session_eur: string;
  fixed_signature_eur: string;
  commission_duration: string;
  org_count: number;
  commission_total_eur: string;
  commission_unpaid_eur: string;
};

export type PartnerCommercials = {
  commercials: CommercialBreakdownRow[];
  unattributed: {
    org_count: number;
    commission_total_eur: string;
    commission_unpaid_eur: string;
  };
};

export type Permission = {
  slug: string;
  description: string;
  category: string;
};

// Phase 3-sso admin
export type OrgIdpConfig = {
  id: string;
  /** "oidc" or "saml" — Phase 3-saml. */
  provider: "oidc" | "saml" | string;
  display_name: string;
  // OIDC fields
  issuer_url: string;
  client_id: string;
  has_client_secret: boolean;
  // SAML fields (Phase 3-saml)
  saml_entity_id: string;
  saml_sso_url: string;
  has_x509_cert: boolean;
  saml_name_id_format: string;
  saml_attribute_email: string;
  saml_attribute_first_name: string;
  saml_attribute_last_name: string;
  // Shared
  email_domains: string[];
  auto_provision_users: boolean;
  default_role_slug: string;
  enabled: boolean;
  discovery_cached_at: string | null;
  created_at: string;
  updated_at: string;
};

export type OrgIdpConfigInput = {
  provider?: "oidc" | "saml";
  display_name?: string;
  // OIDC
  issuer_url?: string;
  client_id?: string;
  client_secret?: string;
  // SAML
  saml_entity_id?: string;
  saml_sso_url?: string;
  saml_x509_cert?: string;
  saml_name_id_format?: string;
  saml_attribute_email?: string;
  saml_attribute_first_name?: string;
  saml_attribute_last_name?: string;
  // Shared
  email_domains?: string[];
  auto_provision_users?: boolean;
  default_role_slug?: string;
  enabled?: boolean;
};

// Phase 2H-kyb
export type KycStatus = "unverified" | "pending" | "verified" | "rejected";

export type OrgKyb = {
  id: number;
  /** Display name (Org.name). Distinct from `legal_name` which is the
   * registered entity name used for VIES + sanctions matching. */
  name: string;
  legal_name: string;
  registration_number: string;
  registration_jurisdiction: string;
  registered_address: string;
  vat_id: string;
  /** VIES canonical name + timestamp. Empty/null when never validated
   * OR when VIES said the VAT ID isn't valid. */
  vat_validated_at: string | null;
  vat_validated_name: string;
  /** Phase 2H-kyb-c — company registry lookup result (Sirene for FR
   * at v1). `registry_validated_name` empty means the registry didn't
   * recognise the registration_number. `registry_is_active` is False
   * when the entity is dissolved / ceased / struck off. */
  registry_validated_at: string | null;
  registry_validated_name: string;
  registry_is_active: boolean;
  /** Phase 2H-kyb-c (DE) — False for jurisdictions where we only
   * validate the format (DE Handelsregister at v1). True for FR
   * (Sirene) + GB (Companies House). UI uses this to render "format
   * validated, verify manually at handelsregister.de" for DE vs
   * "registry says no match" for the FR/GB not-found case. */
  registry_lookup_supported: boolean;
  kyc_status: KycStatus;
  kyc_attested_at: string | null;
  kyc_attested_by_ip: string | null;
  /** Sanctions screening result. Customer admins see the timestamp +
   * boolean flag; match details (which entity flagged us) are
   * deliberately ops-only. */
  sanctions_screened_at: string | null;
  sanctions_match_found: boolean;
};

export type OrgKybInput = {
  legal_name?: string;
  registration_number?: string;
  registration_jurisdiction?: string;
  registered_address?: string;
  vat_id?: string;
  /** Authorized-representative attestation toggle. When `true` on
   * PATCH the backend stamps `kyc_attested_at` + the client IP. */
  attested?: boolean;
};

// Phase 3-scim
export type ScimTokenMeta = {
  id: string;
  token_prefix: string;
  created_at: string;
  last_used_at: string | null;
};

export type ScimTokenIssued = ScimTokenMeta & {
  // Only present in the response from POST — the plaintext is shown
  // once and never re-emitted by the API.
  token: string;
};

// Phase 3-pp-brand — white-label branding shape.
export type Branding = {
  logo_url: string;
  wordmark_url: string;
  favicon_url: string;
  primary_color: string;
  accent_color: string;
  product_name_override: string;
  support_email: string;
  support_url: string;
  email_from_name: string;
  pdf_footer_text: string;
  updated_at: string;
};
export type BrandingInput = Partial<Branding>;

// Phase 3-pp-apikey — per-org API credentials.
export type ApiKey = {
  id: string;
  name: string;
  public_key: string;
  secret_prefix: string;
  scopes: string[];
  created_at: string;
  created_by_email: string | null;
  last_used_at: string | null;
  last_used_ip: string | null;
  revoked_at: string | null;
  expires_at: string | null;
  is_active: boolean;
};
export type ApiKeyIssued = ApiKey & {
  // Plaintext — only returned on POST. Show once.
  secret: string;
};

// Phase 3-pp-webhook — webhook endpoints + delivery log.
export type WebhookEndpoint = {
  id: string;
  url: string;
  description: string;
  event_types: string[];
  signing_secret_prefix: string;
  enabled: boolean;
  consecutive_failures: number;
  created_at: string;
};
export type WebhookEndpointIssued = WebhookEndpoint & {
  signing_secret: string; // only on POST + rotate-secret
};
export type WebhookEvent = {
  id: string;
  endpoint: string;
  event_type: string;
  event_id: string;
  status: "pending" | "delivered" | "failed" | "dropped";
  attempts: number;
  last_attempt_at: string | null;
  last_response_status: number | null;
  last_response_body_truncated: string;
  next_attempt_at: string | null;
  delivered_at: string | null;
  created_at: string;
};

// Phase 3-pp-domain — customer custom domains.
export type CustomDomain = {
  id: string;
  hostname: string;
  verification_token: string;
  verification_target: string;
  verified_at: string | null;
  is_primary: boolean;
  created_at: string;
};
export type CustomDomainVerifyResult = CustomDomain & { ok: boolean };

export type IdpDiscoveryProbeResult =
  | {
      ok: true;
      issuer: string | null;
      authorization_endpoint: string | null;
      token_endpoint: string | null;
      jwks_uri: string | null;
      jwks_keys: number;
    }
  | { ok: false; reason: string; detail: string };

export type SessionStatus =
  | "created"
  | "open"
  | "recording"
  | "paused"
  | "closed"
  | "expired"
  | "cancelled";

export type Plan = {
  slug: "free" | "standard" | "pro" | "enterprise" | string;
  name: string;
  description: string;
  // Legacy fields (back-compat reads only).
  base_price_eur: string;
  seats_included: number;
  extra_seat_per_month_eur: string;
  minutes_included: number;
  overage_per_minute_eur: string;
  // Phase 2F-bill-e — investor-aligned pricing.
  price_per_seat_eur: string;
  price_per_conference_eur: string;
  conference_cap_minutes: number;
  trial_conferences: number;
  annual_discount_pct: number;
  price_signature_l1_eur: string;
  price_signature_l2_eur: string;
  price_signature_l3_eur: string;
  is_self_serve: boolean;
  sort_order: number;
};


export type SubscriptionStatus = "active" | "past_due" | "canceled" | "trialing";


export type Subscription = {
  id: string;
  plan: Plan;
  status: SubscriptionStatus;
  seats_purchased: number;
  // Phase 2F-bill-e — actual seat count + annual flag.
  seat_count: number;
  annual_billing: boolean;
  current_period_start: string;
  current_period_end: string;
  trial_until: string | null;
  created_at: string;
  updated_at: string;
};


export type UsageSummary = {
  // Legacy
  minutes_consumed: number;
  minutes_included: number;
  minutes_remaining: number;
  overage_minutes: number;
  overage_cost_eur: string;
  // Phase 2F-bill-e
  sessions_count: number;
  sessions_cost_eur: string;
  /** Sites beyond the first, across the period's calls. `sessions_count`
   *  is conferences (what the quota counts); the session price is charged
   *  on conferences + this, so without it the cost won't divide by the
   *  call count. See docs/SITES_SCOPE_AND_BILLING.md. */
  extra_site_units: number;
  // Flat per-company base fee (replaces seats, 2026-06-27).
  base_cost_eur: string;
  // Managed (reseller/commercial) accounts hide the self-serve plan grid
  // + public list price and show their own session price (Phase 1 §9a).
  is_managed: boolean;
  effective_session_price_eur: string;
  seat_count: number;
  seats_cost_eur: string;
  signatures_l1_count: number;
  signatures_l2_count: number;
  signatures_l3_count: number;
  signatures_cost_eur: string;
  projected_total_eur: string;
  trial_conferences_total: number;
  trial_conferences_remaining: number;
  signature_pack_credits: {
    l1: number;
    l2: number;
    l3: number;
  };
  period_start: string;
  period_end: string;
};


export type BillingMeResponse = {
  subscription: Subscription;
  usage: UsageSummary;
};


export type Campaign = {
  id: string;
  org: number;
  name: string;
  description: string;
  /** ISO 3166-1 alpha-2 target country (drives country-specific fields). */
  country?: string;
  created_by: number | null;
  /** Inspection slot length in minutes (drives bookable capacity). */
  slot_minutes?: number;
  /** Slot-rhythm config (Campaigns/Sessions v2 #5). */
  break_minutes?: number;
  slack_minutes?: number;
  break_mode?: "none" | "after_n_calls" | "after_minutes";
  calls_before_break?: number;
  minutes_before_break?: number;
  long_break_minutes?: number;
  /** Stamped when the campaign was launched for the booking wave. Null =
   * still a draft. Gates the report button (Campaigns/Sessions v2 #8). */
  launched_at?: string | null;
  /** ISO date (YYYY-MM-DD) — optional planning window the operator
   * sets at create time. Drives the progress bar. */
  start_date: string | null;
  end_date: string | null;
  /** Manual "end campaign" timestamp. Distinct from archived_at —
   * `ended_at` set means the campaign is closed for new sessions
   * but still visible in reports; `archived_at` set means soft-hidden. */
  ended_at: string | null;
  is_ended: boolean;
  archived_at: string | null;
  is_archived: boolean;
  /** standard | public_sector — public_sector campaigns default to the
   * French/STOP booking-wave SMS (#10). */
  campaign_type?: string;
  /** standard | ai_assisted — AI Campaign & Smart Queue. `ai_assisted`
   * runs the outbound SMS/email/voice-AI orchestration alongside the
   * standard manual flow. */
  campaign_kind?: "standard" | "ai_assisted";
  /** Optional IANA tz override for this campaign's beneficiary messages.
   * Blank = inherit the org timezone. */
  timezone?: string;
  session_count: number;
  /** Server-annotated counts; the campaign-list endpoint resolves
   * these in one query via Count(...,filter=Q(...)). Optional in
   * the type because older endpoints might not include them. */
  closed_count?: number;
  live_count?: number;
  /** Timestamp of the most recent session in the campaign. Null
   * when no sessions exist yet. */
  last_session_at?: string | null;
  /** 0..1 completion ratio. Server-computed: time-elapsed when both
   * dates are set, else closed/total sessions, else null. */
  progress: number | null;
  created_at: string;
  updated_at: string;
};


export type Session = {
  id: string;
  org: number;
  operator: number;
  /** Operator email — server-resolved, surfaces in lists + dashboard
   * cards without a follow-up user fetch. Null when the operator
   * row was deleted. */
  operator_email: string | null;
  status: SessionStatus;
  started_at: string | null;
  ended_at: string | null;
  /** Closed-session length in seconds. Null while live, scheduled,
   * or expired-never-joined. */
  duration_seconds: number | null;
  livekit_room_sid: string;
  locale: string;
  consent_state_jsonb: Record<string, "granted" | "denied" | "revoked">;
  geo_jsonb: Record<string, unknown>;
  recording_egress_id: string;
  /** True if any recording-egress column or the N-participant JSON
   * map is populated — drives the "View media" affordance in the
   * sessions list. */
  has_recording: boolean;
  /** Phase 1B-J — server-side anchor for the REC badge. Non-null while
   *  status is `recording`/`paused`; cleared on `/recording/stop`.
   *  Drives both sides' badge bootstrap on page-load + late-join. */
  recording_started_at: string | null;
  /** True when the session already has at least one un-redeemed FIELD
   *  invite — the waiting-room CTA flips from "Invite field user" to
   *  "Send reminder to beneficiary". Computed server-side. */
  has_pending_field_invite: boolean;
  /** Phase 2D-sched-a — optional future-dated start. Null = ad-hoc. */
  scheduled_for: string | null;
  /** Optional human-friendly session name (Campaigns/Sessions v2). */
  title?: string;
  notes: string;
  /** Phase 2E-camp — optional Campaign FK. Null = standalone. */
  campaign: string | null;
  campaign_name: string | null;
  /** Optional CampaignMembership FK this session traces back to. Null
   * for standalone/ad-hoc sessions. Drives the beneficiary-context
   * sidebar panel (identity/address/custom fields) on the call page. */
  membership: string | null;
  /** 2026-09-17 — the beneficiary's display name (legal_name for a
   * legal entity, else "first last"), null for a standalone session
   * with no membership. Every session-list/card/drawer surface shows
   * THIS, never the raw session id. */
  beneficiary_name: string | null;
  /** The linked Contact's id — lets a session card/drawer deep-link
   * straight to the beneficiary's own detail page. */
  beneficiary_contact_id: string | null;
  /** Annotated by the sessions-list endpoint. Counts only `status="ready"`
   * Evidence rows so in-flight uploads don't inflate the number. */
  evidence_count: number;
  /** Distinct participants on the session (includes operator + field +
   * observers + auditors). */
  participant_count: number;
  /** Phase 2F-bill-e — the org's plan cap (180 min by default for paid;
   * 60 for the free trial). Null when unlimited / no subscription. */
  conference_cap_minutes: number | null;
  /** Phase 1B-H-multi — hard ceiling on concurrent participants in this
   * conference (operator + N field/observer/auditor). Null = unlimited
   * (enterprise) — UI should hide the warning entirely in that case. */
  max_participants: number | null;
  created_at: string;
};

/** Aggregate counters returned by GET /v1/dashboard/stats. The
 * dashboard renders the full surface in one round-trip — no follow-
 * up fetches for recent sessions. */
export type DashboardStats = {
  sessions: {
    total: number;
    this_week: number;
    this_month: number;
    by_status: {
      closed: number;
      live: number;
      created: number;
      expired: number;
    };
  };
  evidence: { total: number; this_month: number };
  recordings: { total: number; this_month: number };
  campaigns: { total: number; active: number };
  recent_sessions: Session[];
  /** 30-day timeseries of session counts per UTC day. Zero-filled
   * so the line is contiguous. Drives the dashboard line chart. */
  sessions_timeseries: Array<{ date: string; count: number }>;
  generated_at: string;
};

export type FieldJoinPayload = {
  participant: {
    id: string;
    session: string;
    role: "field" | "observer" | "auditor";
    display_name: string;
    joined_at: string | null;
    left_at: string | null;
  };
  session_id: string;
  /** Phase 1B-J — server-side snapshot of the session's recording
   *  state so the field client can render the REC badge on connect
   *  without depending on a data-channel envelope from the operator. */
  session_status: SessionStatus;
  recording_started_at: string | null;
  first_redemption: boolean;
  livekit: { token: string; url: string };
  /** Short-lived HMAC-signed token authorising backend calls (notably
   * evidence upload) for the duration of this session. The browser
   * sends it as `X-Field-Session-Token`. Phase 1B-B+. */
  field_session_token: string;
};

export type EvidenceKind =
  | "snapshot"
  | "clip"
  | "recording"
  /** Audio of an interview run OUTSIDE the platform (a phone call) and
   *  uploaded afterwards. Deliberately distinct from "recording": the
   *  platform can vouch for an egress it produced, not for what
   *  happened to a phone file before it arrived. */
  | "external_audio"
  | "document"
  | "whiteboard";
export type EvidenceStatus = "pending" | "ready" | "failed" | "tombstoned";

export type Evidence = {
  id: string;
  session: string;
  kind: EvidenceKind;
  mime: string;
  byte_size: number;
  sha256: string;
  storage_key: string;
  uploaded_by: number | null;
  uploaded_by_participant: string | null;
  captured_at: string;
  geo_jsonb: Record<string, unknown>;
  device_jsonb: Record<string, unknown>;
  network_jsonb: Record<string, unknown>;
  integrity_jsonb: {
    sha256_client?: string;
    sha256_verified?: boolean;
    size_verified?: boolean;
    [k: string]: unknown;
  };
  status: EvidenceStatus;
  legal_hold: boolean;
  /** Set when this row is a derivative (e.g. annotated recording) of
   * another. Used by the evidence list to nest annotated copies under
   * the raw recording they were generated from. */
  parent_evidence: string | null;
  /** Presigned S3 GET URL — present on the detail endpoint and on
   * the list endpoint when called with `?with_urls=true`. Null /
   * absent when the row isn't ready yet. */
  download_url?: string | null;
  /** E-signature status, present on document rows involved in a signature
   *  ceremony (media-list endpoint). `role` = "signed_copy" (this row IS a
   *  signed output) or "source" (a signature was requested on this doc);
   *  `signed_pdf_evidence_id` links a source to its signed copy. */
  signature?: {
    role: "source" | "signed_copy";
    status: string;
    signed_pdf_evidence_id?: string | null;
  } | null;
  created_at: string;
  completed_at: string | null;
};

export type EvidenceInitResponse = {
  evidence: Evidence;
  upload_url: string;
  headers: Record<string, string>;
  expires_in_seconds: number;
};

export type AnnotationKind =
  | "freehand"
  | "rect"
  | "ellipse"
  | "text"
  | "polyline"
  | "arrow";

export type FreehandGeometry = { points: [number, number][] };
export type RectGeometry = { x: number; y: number; w: number; h: number };
export type EllipseGeometry = { cx: number; cy: number; rx: number; ry: number };
export type TextGeometry = {
  x: number;
  y: number;
  text: string;
  font_size?: number;
};
export type PolylineGeometry = { points: [number, number][] };
export type ArrowGeometry = { x1: number; y1: number; x2: number; y2: number };
export type AnnotationGeometry =
  | FreehandGeometry
  | RectGeometry
  | EllipseGeometry
  | TextGeometry
  | PolylineGeometry
  | ArrowGeometry;

export type Annotation = {
  id: string;
  session: string;
  evidence: string | null;
  author_user: number | null;
  author_participant: string | null;
  kind: AnnotationKind;
  vector_jsonb: AnnotationGeometry;
  color: string;
  stroke_width: number;
  label: string;
  envelope_msg_id: string;
  created_at: string;
  cleared_at: string | null;
};

/** In-session chat message — see apps/chat/models.py. */
export type ChatMessageKind = "text" | "evidence" | "system";

export type ChatAttachment = {
  id: string;
  /** Original filename from upload (device_jsonb.name) — falls back to
   *  the storage_key basename when missing. Safe to display verbatim. */
  name: string;
  mime: string;
  byte_size: number;
  kind: EvidenceKind;
  storage_key: string;
  status: EvidenceStatus;
  created_at: string | null;
};

export type ChatMessage = {
  id: string;
  session: string;
  author_user: number | null;
  author_participant: string | null;
  kind: ChatMessageKind;
  body: string;
  /** Shape depends on `kind`:
   *  - text:     {}
   *  - evidence: { evidence_id, kind_hint }
   *  - system:   { event, data }
   */
  payload_jsonb: Record<string, unknown>;
  attachments: ChatAttachment[];
  envelope_msg_id: string;
  created_at: string;
};

export type ChatPage = {
  results: ChatMessage[];
  has_more: boolean;
};


export type OrgInvitePreview = {
  id: string;
  org: { id: number; name: string; country: string };
  invited_email: string;
  invited_first_name: string;
  invited_last_name: string;
  role_slugs: string[];
  expires_at: string;
  accepted_at: string | null;
  revoked_at: string | null;
  invited_by_email: string | null;
};